import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = "Select option",
  required = false,
  onQuickAdd,
  addButtonText = "Add New",
  disabled = false,
  className = '',
  controlClassName = '',
  menuClassName = '',
  menuMinWidth = 220
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddInput, setShowAddInput] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const portalRef = useRef(null);

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || rect.width;
      const desiredWidth = Math.max(rect.width, menuMinWidth);
      const width = Math.min(desiredWidth, viewportWidth - 16);
      const left = Math.max(
        8,
        Math.min(rect.left + window.scrollX, window.scrollX + viewportWidth - width - 8)
      );

      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left,
        width
      });
    }
  }, [isOpen, menuMinWidth]);

  const filteredOptions = options.filter(option =>
    option && option.label && option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = options.find(option => option.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          portalRef.current && !portalRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (option) => {
    if (option && typeof option === 'object' && 'value' in option) {
      onChange({ target: { value: option.value } });
    } else if (option) {
      onChange({ target: { value: option } });
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleQuickAdd = async () => {
    if (!newItemName.trim() || !onQuickAdd) return;
    try {
      const newItem = await onQuickAdd(newItemName.trim());
      if (newItem && newItem.value) {
        onChange({ target: { value: newItem.value } });
      }
      setNewItemName('');
      setShowAddInput(false);
      setIsOpen(false);
    } catch (error) {
      console.error('Error adding new item:', error);
    }
  };

  return (
    <div className={`position-relative ${className}`.trim()} ref={dropdownRef}>
      <div
        className={`form-control searchable-select-trigger d-flex justify-content-between align-items-center ${disabled ? 'disabled' : ''} ${controlClassName}`.trim()}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className="text-truncate pe-2">{selectedOption ? selectedOption.label : placeholder}</span>
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
      </div>
      
      {isOpen && !disabled && createPortal(
        <div ref={portalRef} className={`bg-white border rounded shadow-lg ${document.body.getAttribute('data-theme') === 'dark' ? 'searchable-dropdown-dark' : ''} ${menuClassName}`.trim()}
             style={{ 
               position: 'fixed',
               zIndex: 9999, 
               maxHeight: '200px', 
               overflowX: 'hidden',
               top: dropdownPosition.top,
               left: dropdownPosition.left,
               width: dropdownPosition.width
             }}>
            <input
              type="text"
              className="form-control border-0 border-bottom"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
            {filteredOptions.map((option) => (
              <div
                key={option.value}
                className="searchable-option px-3 py-2"
                style={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere'
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSelect(option);
                }}
              >
                {option.label}
              </div>
            ))}
            {filteredOptions.length === 0 && !onQuickAdd && (
              <div className="px-3 py-2 text-muted">No options found</div>
            )}
            {onQuickAdd && (
              <div className="border-top">
                {!showAddInput ? (
                  <div
                    className="searchable-option px-3 py-2 text-primary"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setShowAddInput(true)}
                  >
                    <i className="fas fa-plus me-2"></i>{addButtonText}
                  </div>
                ) : (
                  <div className="p-2">
                    <div className="input-group input-group-sm">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter name"
                        value={newItemName}
                        onChange={(e) => setNewItemName(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleQuickAdd()}
                        autoFocus
                      />
                      <button className="btn btn-success" type="button" onClick={handleQuickAdd}>
                        <i className="fas fa-check"></i>
                      </button>
                      <button className="btn btn-secondary" type="button" onClick={() => { setShowAddInput(false); setNewItemName(''); }}>
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>, document.body
      )}
    </div>
  );
}

export default SearchableSelect;
